import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import pocketbaseService from '../services/pocketbase';

const { FiRefreshCw, FiLogOut, FiServer, FiCheckCircle, FiAlertCircle, FiTrash2, FiAlertTriangle, FiX, FiUser } = FiIcons;

const Settings = ({ connectionInfo, onReconnect }) => {
  const [isTesting, setIsTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [confirmText, setConfirmText] = useState('');

  const handleHealthCheck = async () => {
    setIsTesting(true);
    setTestResult(null);
    try {
      const result = await pocketbaseService.testConnection();
      if (result.provisioned) {
        setTestResult({ 
          success: true, 
          message: 'Infraestrutura GretaBase sincronizada com sucesso. Contexto de Superuser validado.' 
        });
      } else {
        setTestResult({ 
          success: false, 
          message: result.error || 'Falha no provisionamento. Verifique se o PocketBase permite criação de coleções via API.' 
        });
      }
    } catch (err) {
      setTestResult({ success: false, message: 'Erro de comunicação: ' + err.message });
    } finally {
      setIsTesting(false);
    }
  };

  const handleReset = async () => {
    if (confirmText !== 'DELETAR') return;
    setIsTesting(true);
    try {
      const collections = await pocketbaseService.pb.collections.getFullList();
      const toDelete = collections.filter(c => c.name.startsWith('greta_'));
      for (const col of toDelete) {
        await pocketbaseService.pb.collections.delete(col.id);
      }
      setTestResult({ success: true, message: 'Infraestrutura removida com sucesso.' });
      setTimeout(() => window.location.reload(), 1500);
    } catch (err) {
      setTestResult({ success: false, message: 'Erro ao resetar: ' + err.message });
    } finally {
      setIsTesting(false);
      setShowResetConfirm(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-8">
      <div className="mb-10">
        <h1 className="text-2xl font-black text-slate-900 tracking-tight">Preferências</h1>
        <p className="text-slate-400 text-[10px] font-black uppercase tracking-widest mt-1">Configuração de Instância</p>
      </div>

      <div className="grid gap-6">
        <div className="bg-white rounded-[2rem] border border-slate-100 p-8 shadow-sm">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10">
            <div className="flex items-center space-x-4 p-4 bg-slate-50 rounded-2xl">
              <div className="p-3 bg-indigo-50 text-indigo-600 rounded-xl">
                <SafeIcon icon={FiServer} className="text-lg" />
              </div>
              <div className="overflow-hidden">
                <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest block">Core Host</span>
                <span className="text-xs font-bold text-slate-700 truncate block">{connectionInfo.baseUrl}</span>
              </div>
            </div>
            <div className="flex items-center space-x-4 p-4 bg-slate-50 rounded-2xl">
              <div className="p-3 bg-emerald-50 text-emerald-600 rounded-xl">
                <SafeIcon icon={FiUser} className="text-lg" />
              </div>
              <div className="overflow-hidden">
                <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest block">Conectado como</span>
                <span className="text-xs font-bold text-slate-700 truncate block">{connectionInfo.currentUser?.email}</span>
              </div>
            </div>
          </div>

          <div className="flex flex-wrap gap-3">
            <button 
              onClick={handleHealthCheck} 
              disabled={isTesting} 
              className="px-6 py-2.5 bg-indigo-600 text-white rounded-xl font-bold text-xs hover:bg-indigo-700 transition-all flex items-center shadow-lg shadow-indigo-100"
            >
              <SafeIcon icon={FiRefreshCw} className={`mr-2 ${isTesting ? 'animate-spin' : ''}`} />
              Sincronizar e Validar
            </button>
            <button 
              onClick={() => { pocketbaseService.disconnect(); onReconnect(); }} 
              className="px-6 py-2.5 bg-slate-100 text-slate-500 rounded-xl font-bold text-xs hover:bg-red-50 hover:text-red-600 transition-all flex items-center"
            >
              <SafeIcon icon={FiLogOut} className="mr-2" />
              Sair da Conta
            </button>
          </div>

          {testResult && (
            <motion.div 
              initial={{ opacity: 0, y: 10 }} 
              animate={{ opacity: 1, y: 0 }} 
              className={`mt-6 p-4 rounded-xl border flex items-center space-x-3 ${testResult.success ? 'bg-emerald-50 border-emerald-100 text-emerald-700' : 'bg-red-50 border-red-100 text-red-700'}`}
            >
              <SafeIcon icon={testResult.success ? FiCheckCircle : FiAlertCircle} />
              <span className="text-xs font-bold">{testResult.message}</span>
            </motion.div>
          )}
        </div>

        <div className="bg-red-50/30 border border-red-100 rounded-[2rem] p-8 text-center">
          <div className="bg-white w-10 h-10 rounded-xl shadow-sm flex items-center justify-center mx-auto mb-4 text-red-500">
            <SafeIcon icon={FiAlertTriangle} />
          </div>
          <h4 className="text-red-500 text-[10px] font-black uppercase tracking-[0.2em] mb-2">Zona de Perigo</h4>
          <p className="text-slate-400 text-xs font-medium mb-6">A ação de reset remove permanentemente todas as suas pastas e tokens da base de dados.</p>
          <button 
            onClick={() => setShowResetConfirm(true)} 
            className="text-red-600 font-black text-[10px] uppercase tracking-widest hover:underline"
          >
            Resetar Orquestrador Greta
          </button>
        </div>
      </div>

      <AnimatePresence>
        {showResetConfirm && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-6 bg-slate-900/60 backdrop-blur-md">
            <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="bg-white rounded-[2.5rem] w-full max-w-sm p-10 shadow-2xl text-center relative">
              <button onClick={() => setShowResetConfirm(false)} className="absolute top-6 right-6 p-2 hover:bg-slate-100 rounded-lg">
                <SafeIcon icon={FiX} />
              </button>
              <h2 className="text-lg font-black text-slate-900 mb-2">Confirmação Crítica</h2>
              <p className="text-slate-500 text-xs font-medium mb-8">Para deletar todos os dados do sistema, digite <span className="font-black text-red-600">DELETAR</span> abaixo:</p>
              <input 
                type="text" 
                value={confirmText} 
                onChange={e => setConfirmText(e.target.value.toUpperCase())} 
                className="w-full p-4 bg-slate-50 border-2 border-slate-100 rounded-xl text-center font-black text-red-600 mb-6 outline-none focus:border-red-500" 
                placeholder="..." 
              />
              <button 
                onClick={handleReset} 
                disabled={confirmText !== 'DELETAR'} 
                className="w-full py-4 bg-red-600 text-white rounded-xl font-black text-sm disabled:opacity-20 shadow-lg shadow-red-100"
              >
                Sim, Deletar Tudo
              </button>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Settings;